/**
 * The Console's colours, in one place: Lynear's brand palette, and every
 * colour the Console derives from it. Change a colour here and it changes
 * everywhere:
 *
 *   - antd's components and every status tag, through the theme token
 *     (config/theme.js reads this module: the preset tag colours - 'green',
 *     'red', 'gold', 'blue' and the rest - are seeded from the tones below);
 *   - the stylesheets, through CSS variables (paletteCss, which index.js puts
 *     first in the Console's one <style> element; console.css and
 *     registry.css use var(--lynear-...) and hold no colour of their own -
 *     styles/palette.source.test.js holds them to it);
 *   - the few places a colour is passed to a component in JavaScript
 *     (a Badge, the password meter, the notification icons), which import it
 *     from here.
 *
 * LYNEAR_PRIMARY and LYNEAR_SECONDARY are Lynear's palette exactly as given
 * (20 Oct 2026). The tones and neutrals are built from it: a status's text is
 * a palette colour (darkened a little where the palette's own is too light to
 * read on white), its background a pale tint of the same colour. The tints
 * and the greys are the only colours here that are not in the palette, and
 * each is derived from one that is.
 */

/** Lynear's primary palette: the golds, from the darkest. */
export const LYNEAR_PRIMARY = Object.freeze({
    ink: '#1F1A12',
    gold: '#AF9C65',
    goldLight: '#BCAA74',
    sand: '#D7D0A5',
    cream: '#EAE2B4',
    bronze: '#937A47',
    bronzeLight: '#9F8656',
});

/** Lynear's secondary palette. */
export const LYNEAR_SECONDARY = Object.freeze({
    darkCharcoalBrown: '#3A2C28',
    desertSand: '#ADA06B',
    deepCocoa: '#5C3410',
    deepMaroon: '#7C3232',
    oliveGreen: '#5F6443',
    rustClay: '#A54826',
    burntCopper: '#9A6C3F',
    urbanCharcoal: '#59564C',
    sandyTaupe: '#A78C63',
    tealOcean: '#0F5E6B',
    goldenOchre: '#D6A84F',
});

const P = LYNEAR_PRIMARY;
const S = LYNEAR_SECONDARY;

/**
 * Neutrals for text, lines and surfaces - warm greys taken from Dark
 * Charcoal Brown and Urban Charcoal, kept light (Lynear, 20 Oct 2026), so the
 * Console's greys sit with the golds.
 */
export const NEUTRAL = Object.freeze({
    white: '#FFFFFF',
    heading: S.darkCharcoalBrown,          // headings and strong text
    text: S.urbanCharcoal,                 // body text
    textSecondary: '#6E6B62',              // Urban Charcoal, a little lighter
    muted: '#86827A',                      // labels and secondary text: Urban Charcoal, lighter
    subtle: '#A8A49B',                     // placeholders, disabled text: lighter still
    line: '#E8E5DE',                       // borders and dividers
    surface: '#F8F7F3',                    // panels, table heads, quiet backgrounds
})

/** The golds the Console uses beyond the palette's own: a darker one for text on light gold, and pale tints for backgrounds. */
export const GOLD = Object.freeze({
    base: P.gold,
    dark: P.bronze,              // hover and pressed
    text: '#786D4F',             // gold text on a light background: Bronze, darkened to read on white
    border: P.sand,
    tint: '#F4EFD3',             // Cream, paler: selected rows, highlighted panels
    wash: '#FBF8EA',             // Cream, palest
});

// ---------------------------------------------------------------------------
// Deriving a tone from one palette colour: a pale tint behind it, a softer
// border, and its text darkened only as far as it must be to read.
// ---------------------------------------------------------------------------

const channels = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
const toHex = (rgb) => '#' + rgb.map((c) => Math.round(c).toString(16).padStart(2, '0')).join('').toUpperCase();

/** `hex` moved `share` (0 to 1) of the way towards `toward`: mix('#7C3232', '#FFFFFF', 0.9) is a pale maroon. */
export function mix(hex, toward, share) {
    const a = channels(hex);
    const b = channels(toward);
    return toHex(a.map((c, i) => c + (b[i] - c) * share));
}

/** WCAG relative luminance and contrast ratio, so a status's text is always readable on its tint. */
const luminance = (hex) => {
    const [r, g, b] = channels(hex).map((c) => {
        const v = c / 255;
        return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
export function contrast(a, b) {
    const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
    return (hi + 0.05) / (lo + 0.05);
}

/**
 * The lightest text of a palette colour that still reads on `bg` (WCAG AA,
 * 4.5:1): a dark colour (Deep Cocoa, Dark Charcoal Brown, Teal Ocean) is
 * lifted towards white as far as it can go; a light one (Golden Ochre, Desert
 * Sand) is darkened towards the ink only as far as it must be. Light, as
 * Lynear asked (20 Oct 2026), and always readable.
 */
function readable(hex, bg) {
    if (contrast(hex, bg) >= 4.5) {
        let text = hex;
        for (let step = 1; step <= 20; step += 1) {
            const lighter = mix(hex, NEUTRAL.white, step * 0.025);
            if (contrast(lighter, bg) < 4.5) break;
            text = lighter;
        }
        return text;
    }
    let text = hex;
    for (let step = 1; contrast(text, bg) < 4.5 && step <= 20; step += 1) text = mix(hex, P.ink, step * 0.05);
    return text;
}

/**
 * A status tone from one palette colour: text (the colour, as light as reads
 * on its tint), strong (the colour as it is - icons, marks, solid fills), bg
 * (a very pale tint of it) and border (a soft one).
 */
function toneOf(base, { strong = base, bg = mix(base, NEUTRAL.white, 0.93), border = mix(base, NEUTRAL.white, 0.72) } = {}) {
    return Object.freeze({ text: readable(base, bg), strong, bg, border });
}

/**
 * The status tones. Every status in the Console is one of these, each built
 * from one palette colour:
 *
 *   success   done, approved, posted, matched              Olive Green
 *   danger    rejected, failed, refused, a break           Deep Maroon (Rust Clay where it must stand out)
 *   warning   waiting, needs a person, a part payment      Burnt Copper (Golden Ochre for marks)
 *   info      open, in progress, being processed           Teal Ocean
 *   pending   proposed, awaiting a decision                the brand gold
 *   special   overridden, estimated, out of the ordinary   Deep Cocoa
 *   neutral   ended without fault, not applicable          Urban Charcoal
 */
export const TONE = Object.freeze({
    success: toneOf(S.oliveGreen),
    danger: toneOf(S.deepMaroon, { strong: S.rustClay }),
    warning: toneOf(S.burntCopper, { strong: S.goldenOchre }),
    info: toneOf(S.tealOcean),
    pending: toneOf(P.bronze, { strong: P.gold, bg: GOLD.tint, border: P.sand }),
    special: toneOf(S.deepCocoa),
    neutral: toneOf(S.urbanCharcoal, { bg: NEUTRAL.surface, border: NEUTRAL.line }),
});

/**
 * antd's preset tag colours, each given a tone of its own, so every
 * <Tag color="green">, "gold", "blue" and the rest follows Lynear's palette
 * without the screens changing their words. The presets a screen sets side
 * by side stay apart - a transaction goes Validated (blue) -> Funds cleared
 * (cyan) -> Book closed (geekblue) -> Priced (lime) -> Ready (green) - so each
 * takes its own palette colour.
 *
 * success, error, warning, processing and default are antd's status presets.
 */
export const PRESET_TONES = Object.freeze({
    blue: TONE.info,                                    // open, in progress
    cyan: toneOf(S.desertSand),                         // money matched and cleared
    geekblue: toneOf(S.darkCharcoalBrown),              // book closed, frozen
    lime: toneOf(S.sandyTaupe),                         // priced
    green: TONE.success,                                // ready, approved, posted, passed
    gold: toneOf(S.goldenOchre),                        // waiting for money, proposed, a warning
    yellow: toneOf(S.goldenOchre),
    orange: TONE.warning,                               // waiting for clearance, needs a person
    volcano: toneOf(S.rustClay),                        // held
    red: TONE.danger,                                   // rejected, failed
    magenta: TONE.danger,
    pink: TONE.danger,
    purple: TONE.special,                               // overridden, estimated, out of the ordinary
    success: TONE.success,
    error: TONE.danger,
    warning: toneOf(S.goldenOchre),
    processing: TONE.info,
    default: TONE.neutral,
});

/**
 * The same presets as antd theme seeds (config/theme.js): what antd's other
 * components - a Badge's dot, a Steps icon, a Progress bar - draw in. The
 * palette colour as it is.
 */
export const PRESET_SEEDS = Object.freeze(Object.fromEntries(Object.entries(PRESET_TONES)
    .filter(([name]) => !['success', 'error', 'warning', 'processing', 'default'].includes(name))
    .map(([name, tone]) => [name, tone.strong])));

/** The CSS variable for a palette name: tone.danger.bg -> --lynear-danger-bg. */
const kebab = (name) => name.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase());

/**
 * Every colour above as a CSS custom property, under .lfoc-styles-wrapper (the
 * Console's own scope - it is a guest in the LIV page):
 *
 *   --lynear-<primary name>     e.g. --lynear-gold, --lynear-bronze
 *   --lynear-<secondary name>   e.g. --lynear-olive-green, --lynear-teal-ocean
 *   --lynear-<neutral name>     e.g. --lynear-heading, --lynear-text, --lynear-line, --lynear-surface
 *   --lynear-gold-<name>        e.g. --lynear-gold-text, --lynear-gold-tint
 *   --lynear-<tone>-<part>      e.g. --lynear-danger-text, --lynear-success-bg
 *
 * plus the older --brand-* names the stylesheets used before 20 Oct 2026, now
 * pointing at the palette.
 *
 * @returns {string} one CSS rule
 */
export function paletteCss() {
    const vars = [];
    for (const [name, hex] of Object.entries(LYNEAR_PRIMARY)) vars.push(['--lynear-' + kebab(name), hex]);
    for (const [name, hex] of Object.entries(LYNEAR_SECONDARY)) vars.push(['--lynear-' + kebab(name), hex]);
    for (const [name, hex] of Object.entries(NEUTRAL)) vars.push(['--lynear-' + kebab(name), hex]);
    for (const [name, hex] of Object.entries(GOLD)) if (name !== 'base') vars.push(['--lynear-gold-' + kebab(name), hex]);
    for (const [tone, parts] of Object.entries(TONE)) for (const [part, hex] of Object.entries(parts)) vars.push(['--lynear-' + tone + '-' + part, hex]);
    vars.push(
        ['--brand-primary', 'var(--lynear-gold)'],
        ['--brand-primary-dark', 'var(--lynear-gold-dark)'],
        ['--brand-primary-shade', 'color-mix(in srgb, var(--lynear-gold) 10%, transparent)'],
        ['--brand-black', 'var(--lynear-text)'],
        ['--brand-gray', 'var(--lynear-surface)'],
        ['--brand-light-grey', 'var(--lynear-line)'],
    );
    // Each preset tag in its palette tone. Five classes, so it outranks antd's own
    // (:where(.css-...).lfoc-tag.lfoc-tag-green:not(.lfoc-tag-disabled).lfoc-tag-outlined: four), whatever order the sheets load in.
    const tags = Object.entries(PRESET_TONES).map(([preset, tone]) => '.lfoc-styles-wrapper .lfoc-tag.lfoc-tag-' + preset
        + ':not(.lfoc-tag-disabled):not(.lfoc-tag-solid) { color: ' + tone.text + '; background: ' + tone.bg + '; border-color: ' + tone.border + '; }');
    return '/* Lynear palette (config/palette.js): the Console\'s only colours. */\n.lfoc-styles-wrapper {\n'
        + vars.map(([name, value]) => '    ' + name + ': ' + value + ';').join('\n') + '\n}\n' + tags.join('\n') + '\n';
}
